// Enhanced Phonetics Service with Google TTS Integration
// Uses enhanced Web Speech API with Google-quality phonetic mappings

class EnhancedPhoneticsService {
  constructor() {
    this.initialized = false;
    this.voices = [];
    this.preferredVoice = null;
  }

  // Initialize the service
  async initialize() {
    if (this.initialized) return;

    try {
      await this.loadVoices();
      this.initialized = true;
    } catch (error) {
      this.initialized = true; // Still mark as initialized to use fallbacks
    }
  }

  // Load and categorize available voices
  async loadVoices() {
    return new Promise((resolve) => {
      const loadVoices = () => {
        this.voices = speechSynthesis.getVoices();
        
        // Prefer high-quality voices
        const preferredVoices = [
          'Google US English',
          'Microsoft Zira Desktop',
          'Microsoft Hazel Desktop',
          'Samantha',
          'Karen',
          'Moira',
          'Tessa',
          'Alex',
          'Susan'
        ];

        for (const preferredName of preferredVoices) {
          const voice = this.voices.find(v => v.name.includes(preferredName));
          if (voice) {
            this.preferredVoice = voice;
            break;
          }
        }

        if (!this.preferredVoice && this.voices.length > 0) {
          this.preferredVoice = this.voices.find(v => v.lang.includes('en')) || this.voices[0];
        }

        resolve();
      };

      if (speechSynthesis.getVoices().length > 0) {
        loadVoices();
      } else {
        speechSynthesis.addEventListener('voiceschanged', loadVoices);
      }
    });
  }


  // Play phoneme with enhanced quality
  async playPhoneme(letter, options = {}) {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      await this.playWithEnhancedWebSpeech(letter, options);
    } catch (error) {
      await this.basicWebSpeech(letter, options);
    }
  }

  // Play using enhanced Web Speech API
  async playWithEnhancedWebSpeech(letter, options = {}) {
    const phoneticMapping = this.getPhoneticMapping(letter);
    
    return new Promise((resolve, reject) => {
      if (!('speechSynthesis' in window)) {
        reject(new Error('Speech synthesis not supported'));
        return;
      }

      // Cancel any ongoing speech
      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(phoneticMapping);
      
      // Configure for kindergarten phonetics
      utterance.rate = 0.8; // Slower for learning
      utterance.pitch = 1.2; // Higher pitch for children
      utterance.volume = options.volume || 0.9;
      
      // Use preferred voice
      if (this.preferredVoice) {
        utterance.voice = this.preferredVoice;
      }

      utterance.onend = () => {
        // Small delay between phonemes
        setTimeout(resolve, 50);
      };

      utterance.onerror = (event) => {
        reject(event.error);
      };

      speechSynthesis.speak(utterance);
    });
  }

  // Get phonetic mapping for kindergarten learning
  getPhoneticMapping(letter) {
    const mappings = {
      // Short vowels - clear, distinct sounds (phonetic pronunciation)
      'a': 'ah',      // cat, apple - short a sound
      'e': 'eh',      // bed, egg - short e sound  
      'i': 'ih',      // pig, sit - short i sound
      'o': 'aw',      // hot, dot - short o sound
      'u': 'uh',      // cup, sun - short u sound
      
      // Long vowels - letter names (when appropriate)
      'A': 'ay',      // acorn, ape - long a sound
      'E': 'e',      // eat, eagle - long e sound
      'I': 'eye',     // ice, island - long i sound
      'O': 'oh',      // ocean, open - long o sound
      'U': 'you',     // unicorn, use - long u sound
      
      // Consonants - phonetic sounds using TTS-friendly words
      'b': 'buh',     // bat, ball - /b/ sound
      'c': 'ca',     // cat, cup - /k/ sound (hard c)
      'd': 'dah',     // dog, door - /d/ sound
      'f': 'fuh',     // fish, fan - /f/ sound (continuous)
      'g': 'guh',     // goat, game - /g/ sound (hard g)
      'h': 'huh',     // hat, house - /h/ sound (breathy)
      'j': 'juh',     // jam, jump - /dʒ/ sound
      'k': 'kuh',     // kite, kick - /k/ sound
      'l': 'luh',     // lion, lamp - /l/ sound
      'm': 'muh',       // mom, milk - /m/ sound (continuous single letter)
      'n': 'na',       // nest, nose - /n/ sound (continuous single letter)
      'p': 'puh',     // pig, pen - /p/ sound
      'q': 'kuh',     // queen, quick - /kw/ sound (using 'kuh' for better TTS)
      'r': 'r',      // rabbit, run - /r/ sound (using 'er' for better TTS)
      's': 'suh',       // snake, sun - /s/ sound (continuous single letter)
      't': 'tuh',     // top, turtle - /t/ sound
      'v': 'vuh',       // van, violin - /v/ sound (continuous single letter)
      'w': 'wuh',     // water, window - /w/ sound
      'x': 'ks',     // box, fox - /ks/ sound (using 'eks' for better TTS)
      'y': 'yuh',     // yellow, yes - /j/ sound (as in yes)
      'z': 'zuh',       // zoo, zipper - /z/ sound (continuous single letter)
    };

    return mappings[letter] || letter;
  }


  // Basic Web Speech fallback
  async basicWebSpeech(letter, options = {}) {
    return new Promise((resolve) => {
      if ('speechSynthesis' in window) {
        const phoneticMapping = this.getPhoneticMapping(letter);
        const utterance = new SpeechSynthesisUtterance(phoneticMapping);
        utterance.rate = 0.7;
        utterance.pitch = 1.1;
        utterance.volume = options.volume || 0.8;
        
        utterance.onend = resolve;
        speechSynthesis.speak(utterance);
      } else {
        resolve();
      }
    });
  }

  // Check if service is initialized
  isInitialized() {
    return this.initialized;
  }

  // Get available phonemes
  getAvailablePhonemes() {
    return [
      'a', 'e', 'i', 'o', 'u',
      'A', 'E', 'I', 'O', 'U',
      'b', 'c', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm',
      'n', 'p', 'q', 'r', 's', 't', 'v', 'w', 'x', 'y', 'z'
    ];
  }

  // Get current voice info
  getVoiceInfo() {
    return {
      currentVoice: this.preferredVoice?.name || 'Default',
      availableVoices: this.voices.length
    };
  }
}

// Export singleton instance
export const enhancedPhoneticsService = new EnhancedPhoneticsService();
